/**
 * IntercomBooter
 *
 * Headless component that boots Intercom with verified user identity.
 * Mounted at App root level, inside IntercomProvider and AuthProvider.
 *
 * Flow:
 * 1. IntercomProvider initializes the Intercom script (no autoBoot)
 * 2. On login: fetches JWT from builder-service, then calls boot() with intercomUserJwt
 * 3. On logout: shutdown() to clear session cookies
 *
 * Important: autoBoot is DISABLED in IntercomProvider. We boot manually here
 * because api-iam.intercom.io requires JWT from the very first request.
 * Anonymous boot to IAM endpoint results in 403.
 */
import { useEffect, useRef } from 'react';
import { useIntercom } from 'react-use-intercom';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';

const BUILDER_API_URL = import.meta.env.VITE_BUILDER_API_URL || 'https://api.segmently.ai';

/**
 * Fetch signed JWT from builder-service for Intercom Identity Verification.
 * Returns null on failure (Intercom won't boot — no anonymous fallback on IAM).
 */
async function fetchUserJwt(): Promise<{ token: string | null; email: string | null; enabled?: boolean } | null> {
  try {
    const firebaseToken = await auth.currentUser?.getIdToken();
    if (!firebaseToken) return null;

    const response = await fetch(`${BUILDER_API_URL}/api/intercom/user-jwt`, {
      headers: { Authorization: `Bearer ${firebaseToken}` },
    });

    if (!response.ok) {
      console.warn('[IntercomBooter] Failed to fetch user JWT:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('[IntercomBooter] Error fetching user JWT:', error);
    return null;
  }
}

export const IntercomBooter = () => {
  const { boot, shutdown } = useIntercom();
  const { currentUser } = useAuth();
  const prevUserRef = useRef<string | null>(null);

  useEffect(() => {
    if (currentUser) {
      // Fetch JWT and boot Intercom with verified identity
      fetchUserJwt().then((result) => {
        if (result?.token) {
          // Boot with JWT — identity is verified from the first request.
          // apiBase is configured on IntercomProvider; react-use-intercom does not map it from boot props.
          boot({
            userId: currentUser.uid,
            email: currentUser.email || result.email || undefined,
            name: currentUser.displayName || currentUser.email || 'User',
            intercomUserJwt: result.token,
            hideDefaultLauncher: true,
          });
          console.log('[IntercomBooter] Booted with JWT identity');
        } else {
          console.warn('[IntercomBooter] JWT fetch failed — Intercom not booted');
        }
      });
      prevUserRef.current = currentUser.uid;
    } else if (prevUserRef.current) {
      // User logged out — shutdown Intercom completely
      shutdown();
      prevUserRef.current = null;
      console.log('[IntercomBooter] Shutdown on logout');
    }
  }, [currentUser, boot, shutdown]);

  return null;
};

export default IntercomBooter;
